import {
  useEffect,
  useId,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';
import { IconButton } from './IconButton';
import { Button } from './Button';

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Optional heading. When set, a header row is shown. */
  title?: ReactNode;
  /**
   * Show the header close (X) control.
   * Defaults to true when `title` is provided, false otherwise.
   */
  showClose?: boolean;
  primaryAction?: {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'danger';
  };
  secondaryAction?: { label: string; onClick: () => void };
  /** Applied to the dialog panel (e.g. max-w-2xl). Default max-w-lg. */
  className?: string;
  id?: string;
};

/**
 * Modal surface — solid + dialog shadow.
 * Portals to `document.body` so nesting under a form cannot associate
 * dialog controls with that form (Enter → accidental submit).
 * Prefer inline edit over dialogs for single fields.
 */
export function Dialog({
  open,
  title,
  children,
  onClose,
  showClose,
  primaryAction,
  secondaryAction,
  className,
  id,
}: DialogProps) {
  const titleId = useId();
  const hasTitle = title != null && title !== false && title !== '';
  const showCloseButton = showClose ?? hasTitle;
  const hasHeader = hasTitle || showCloseButton;
  const hasFooter = Boolean(primaryAction || secondaryAction);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const panel = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:px-6">
      <button
        type="button"
        aria-label="Close dialog backdrop"
        className="absolute inset-0 bg-ds-text/30"
        onClick={onClose}
      />
      <div
        id={id}
        role="dialog"
        aria-modal="true"
        {...(hasTitle ? { 'aria-labelledby': titleId } : {})}
        className={cn(
          'relative z-10 max-h-full w-full overflow-auto rounded-ds-lg border border-ds-border bg-ds-surface shadow-ds-dialog',
          className ?? 'max-w-lg',
        )}
      >
        {hasHeader || hasFooter ? (
          <div className="p-6">
            {hasHeader ? (
              <div className="mb-5 flex items-center justify-between gap-3">
                {hasTitle ? (
                  <h2
                    id={titleId}
                    className="text-ds-lg font-semibold tracking-tight text-ds-text"
                  >
                    {title}
                  </h2>
                ) : (
                  <span />
                )}
                {showCloseButton ? (
                  <IconButton label="Close" onClick={onClose}>
                    <X />
                  </IconButton>
                ) : null}
              </div>
            ) : null}
            <div className="text-ds-sm text-ds-text">{children}</div>
            {hasFooter ? (
              <div className="mt-6 flex flex-wrap justify-end gap-2 border-t border-ds-border pt-4">
                {secondaryAction ? (
                  <Button variant="secondary" onClick={secondaryAction.onClick}>
                    {secondaryAction.label}
                  </Button>
                ) : null}
                {primaryAction ? (
                  <Button
                    variant={
                      primaryAction.variant === 'danger' ? 'danger' : 'primary'
                    }
                    onClick={primaryAction.onClick}
                  >
                    {primaryAction.label}
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );

  return createPortal(panel, document.body);
}
